import { i18n } from '@/i18n';
import { request, windowHelper } from '@tencent/ppd-base-libs';
import { ErrorCodesType, SimpleLogin, loginConfig } from '@tencent/wphk-act-base-api';
import { setRequest } from '@tencent/wphk-act-base-cgi';
import { axios } from '@tencent/wphk-base-request';
import { hideLoading } from '@tencent/wphk-components-loading';
import { initRequestBlockInterceptors } from '@tencent/wphk-components-project-common';
import { hideDialog, hideToast, showDialog, showToast } from '@tencent/wphk-design';
import { isInMiniProgram } from '@tencent/wphk-utils';

request.init(axios);
setRequest(request);

// ---------- 登录拦截状态标识 ----------

// eslint-disable-next-line @typescript-eslint/naming-convention
let _isNotHkUser = false;
// eslint-disable-next-line @typescript-eslint/naming-convention
let _isLoginErrorHandled = false;

export function getIsNotHkUser(): boolean {
  return _isNotHkUser;
}

/** 登录拦截器是否已处理过错误（非港用户/账户异常/OAuth 错误码等） */
export function getIsLoginErrorHandled(): boolean {
  return _isLoginErrorHandled;
}

// ---------- Promise 桥接：页面投喂非港用户重定向配置 ----------

export interface RedirectConfig {
  /** CDN 配置是否加载成功 */
  isConfigSucc: boolean;
  /** 各渠道重定向 URL */
  redirectUrl?: { h5?: string; mp?: string };
}

// eslint-disable-next-line @typescript-eslint/naming-convention
let _resolveRedirectConfig: (config: RedirectConfig) => void;
// eslint-disable-next-line @typescript-eslint/naming-convention
const _redirectConfigReady = new Promise<RedirectConfig>((r) => {
  _resolveRedirectConfig = r;
});

export function setRedirectConfig(config: RedirectConfig) {
  _resolveRedirectConfig(config);
}

// ---------- 商户页路由豁免（精确匹配 hash pathname） ----------

const NON_HK_EXEMPT_ROUTES = ['/merchant'];

const REDIRECT_TIMEOUT_MS = 3000;

const errorHandleConfig: Record<PropertyKey, (err: any, executeDefault?: any) => void> = {
  [ErrorCodesType.IS_NOT_HK_USER_CODES]: (_err: any, executeDefault: (flag: boolean) => void) => {
    _isNotHkUser = true;
    _isLoginErrorHandled = true;
    const hashPath = new URL(location.hash.replace(/^#/, ''), location.origin).pathname;
    if (NON_HK_EXEMPT_ROUTES.includes(hashPath)) return;

    showToast({ icon: 'loading', message: '', autoHide: false });

    Promise.race([
      _redirectConfigReady,
      new Promise<RedirectConfig>(r => setTimeout(() => r({ isConfigSucc: false }), REDIRECT_TIMEOUT_MS)),
    ]).then((config) => {
      const redirectUrl = isInMiniProgram() ? config.redirectUrl?.mp : config.redirectUrl?.h5;
      if (redirectUrl) {
        windowHelper.replace(redirectUrl);
      } else {
        executeDefault(true);
      }
      setTimeout(hideToast, 2000);
    });
  },
  // 账户冻结/异常
  [ErrorCodesType.ACCOUNT_ABNORMAL_RETCODE]: (err: any) => {
    _isLoginErrorHandled = true;
    showDialog({
      title: err.retmsg,
      actions: [{ text: i18n('我知道了'), click: hideDialog }],
    });
  },
  // OAuth 授权码错误
  [ErrorCodesType.OAUTH_CODE_ERROR_CODES]: (_err: any, executeDefault?: (flag: boolean) => void) => {
    _isLoginErrorHandled = true;
    executeDefault?.(true);
  },
  // 有标 + 无UID + 曾注销过用户：跳转 self_operate 自助开通确认页（registerUid 中间页）。
  // 不走 IS_NOT_HK_USER_CODES 的 CDN 重定向，直接由默认 handler 跳新链接。
  [ErrorCodesType.IS_LOGGED_OUT_HK_USER_CODES]: (_err: any, executeDefault?: (flag: boolean) => void) => {
    _isLoginErrorHandled = true;
    executeDefault?.(true);
  },
};

const simpleLogin = new SimpleLogin(request, {
  errorHandleConfig,
  // 业务方标识：用于 elk / 上报维度；跳转 self_operate registerUid 页时作为 ?channel= 透传包名，
  // 由 self_operate 的 BUSINESS_CHANNEL_MAP 反查注册渠道（fstandby4）数字，提交 submit 时透传数字。
  // @ts-ignore 项目未引入 @types/node，process 类型沿用 main.ts 同款规避
  business: process.env.VUE_APP_PKGNAME,
} as any);

initRequestBlockInterceptors({
  blockingRequest: {
    beforeLogin() {
      hideLoading();
      hideToast();
    },
    getOAuthCode() {
      simpleLogin.getWXOAuthCodeByJumpToWx({
        url: location.href,
      });
    },
    login: async (config) => {
      if (config.url === loginConfig.LoginSimpleApi) return;
      if (config.url?.startsWith('https://st.moneydata.hk/')) return;
      if (config.url?.includes('/jsapi_signature.cgi')) return;
      await simpleLogin.waitLoginPromiseHandle();
    },
  },
});
